import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Save, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import { getSystemEmojis, updateSystemEmoji } from '../lib/api';
import EmojiPicker from '../components/EmojiPicker';
import { DEFAULT_CATEGORIES } from '../lib/categories';

export default function AdminCategoriesPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [systemEmojis, setSystemEmojis] = useState({});
  const [editingCategory, setEditingCategory] = useState(null);
  const [pendingChanges, setPendingChanges] = useState({});

  useEffect(() => {
    loadSystemEmojis();
  }, []);

  const loadSystemEmojis = async () => {
    try {
      setLoading(true);
      const res = await getSystemEmojis();
      setSystemEmojis(res.data.emojis || {});
    } catch (error) {
      console.error('Error loading system emojis:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const getEffectiveEmoji = (cat) => {
    return pendingChanges[cat.id] || systemEmojis[cat.id] || cat.emoji;
  };

  const handleEmojiChange = (categoryId, emoji) => {
    setPendingChanges(prev => ({ ...prev, [categoryId]: emoji }));
    setEditingCategory(null);
  };

  const handleSave = async (categoryId) => {
    const newEmoji = pendingChanges[categoryId];
    if (!newEmoji) return;

    try {
      await updateSystemEmoji(categoryId, newEmoji);
      setSystemEmojis(prev => ({ ...prev, [categoryId]: newEmoji }));
      setPendingChanges(prev => {
        const newChanges = { ...prev };
        delete newChanges[categoryId];
        return newChanges;
      });
      toast.success('Emoji système mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleReset = (categoryId) => {
    setPendingChanges(prev => {
      const newChanges = { ...prev };
      delete newChanges[categoryId];
      return newChanges;
    });
  };

  const labels = {
    fr: {
      title: 'Emojis des catégories système',
      subtitle: 'Personnalisez les emojis par défaut pour tous les utilisateurs',
      default: 'Par défaut',
      modified: 'Modifié',
      save: 'Sauvegarder',
      reset: 'Annuler',
      clickToEdit: 'Cliquez sur un emoji pour le modifier',
      pendingChanges: 'modifications en attente'
    },
    en: {
      title: 'System Category Emojis',
      subtitle: 'Customize default emojis for all users',
      default: 'Default',
      modified: 'Modified',
      save: 'Save',
      reset: 'Cancel',
      clickToEdit: 'Click an emoji to edit',
      pendingChanges: 'pending changes'
    },
    pt: {
      title: 'Emojis das categorias do sistema',
      subtitle: 'Personaliza os emojis padrão para todos os utilizadores',
      default: 'Padrão',
      modified: 'Modificado',
      save: 'Guardar',
      reset: 'Cancelar',
      clickToEdit: 'Clica num emoji para editar',
      pendingChanges: 'alterações pendentes'
    }
  };

  const t = labels[language] || labels.fr;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-8">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-bold text-lg">{t.title}</h1>
                <p className="text-sm text-muted-foreground">{t.subtitle}</p>
              </div>
            </div>
            {Object.keys(pendingChanges).length > 0 && (
              <span className="text-sm text-amber-600 bg-amber-50 px-3 py-1 rounded-full">
                {Object.keys(pendingChanges).length} {t.pendingChanges}
              </span>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center justify-between">
                <span>{t.clickToEdit}</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {DEFAULT_CATEGORIES.map((cat) => {
                  const hasChange = pendingChanges[cat.id];
                  const isEditing = editingCategory?.id === cat.id;
                  
                  return (
                    <div
                      key={cat.id}
                      className={`p-4 rounded-xl border ${hasChange ? 'border-amber-400 bg-amber-50/50 dark:bg-amber-900/10' : 'border-border'} transition-all`}
                    >
                      <div className="flex items-center gap-3">
                        {isEditing ? (
                          <EmojiPicker
                            value={getEffectiveEmoji(cat)}
                            onChange={(emoji) => handleEmojiChange(cat.id, emoji)}
                          />
                        ) : (
                          <button
                            onClick={() => setEditingCategory(cat)}
                            className="w-14 h-14 rounded-xl flex items-center justify-center text-3xl bg-muted hover:bg-muted/80 hover:scale-105 transition-all cursor-pointer"
                          >
                            {getEffectiveEmoji(cat)}
                          </button>
                        )}
                        
                        <div className="flex-1">
                          <p className="font-medium">
                            {language === 'fr' ? cat.name_fr : language === 'pt' ? cat.name_pt : cat.name_en}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {t.default}: {cat.emoji}
                            {systemEmojis[cat.id] && systemEmojis[cat.id] !== cat.emoji && (
                              <span className="ml-2 text-primary">→ {systemEmojis[cat.id]}</span>
                            )}
                          </p>
                          {hasChange && (
                            <p className="text-xs text-amber-600 mt-1">{t.modified}</p>
                          )}
                        </div>
                        
                        {hasChange && (
                          <div className="flex flex-col gap-1">
                            <Button size="sm" onClick={() => handleSave(cat.id)}>
                              <Save className="w-3 h-3 mr-1" />
                              {t.save}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleReset(cat.id)}>
                              <RotateCcw className="w-3 h-3 mr-1" />
                              {t.reset}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
}
